import { useCallback, useEffect, useRef, useState } from "react";
import Sentry from "@similarweb/sentry-logger";

import * as countryUtils from "app/components/filters-bar/utils";
import type { ICountryObject } from "app/services/CountryService";
import { allTrackers } from "app/services/track/track";

import type { CreatedIndustry } from "../../CustomHooks/customIndustryAgent.types";
import { useCustomIndustryAgent } from "../../CustomHooks/useCustomIndustryAgent";
import { useSegmentBudget } from "../../CustomHooks/useSegmentBudget";
import { openCreatedIndustry } from "../../services/openCreatedIndustry";
import { ComposeStep } from "./ComposeStep";
import { GeneratingStep } from "./GeneratingStep";
import { getRejectionComposeMessage } from "./rejectionCopy";
import { translateWithFallback as t } from "./translateWithFallback";

/** One automatic retry, because the only thing that can reject in the hand-off is a plain
 *  refresh request and the industry itself already exists, so retrying redoes nothing
 *  server-side. Past that the run is parked on an error. */
const MAX_HANDOFF_RETRIES = 1;

type AiCustomIndustryBuilderV2Props = {
    selectedCountry: ICountryObject;
    onSelectedCountryChange: (country: ICountryObject) => void;
    /** Puts back the country a rejected or failed run was started with, without counting as a
     *  user pick. */
    onRestoreCountry: (country: ICountryObject) => void;
    onIndustryCreated: (industry: CreatedIndustry, country: ICountryObject | undefined) => void;
    onFallThrough: () => void;
    onClose: () => void;
    /** Opened from the failed run's toast. Any other way in lets a failed run go unseen. */
    hasArrivedForRunError: boolean;
};

export const AiCustomIndustryBuilderV2 = ({
    selectedCountry,
    onSelectedCountryChange,
    onRestoreCountry,
    onIndustryCreated,
    onFallThrough,
    onClose,
    hasArrivedForRunError,
}: AiCustomIndustryBuilderV2Props) => {
    const [handoffAttempt, setHandoffAttempt] = useState(0);
    const {
        state,
        industry,
        errorCode,
        description: runDescription,
        countryId: runCountryId,
        isRunBlocking,
        generate,
        cancel,
        dismiss,
        dismissError,
        reportHandoffFailure,
    } = useCustomIndustryAgent();
    const { maxSegments, hasSegmentsModule, isLoading: isSegmentBudgetLoading, requestBudget } = useSegmentBudget();
    const handedOffRef = useRef(false);
    const hasRestoredCountryRef = useRef(false);
    const countryId = selectedCountry.id;
    // A run that failed before this builder opened is hidden from the first render on, so
    // neither its banner nor its country flashes up before it is dismissed.
    const [hasStaleRunError, setHasStaleRunError] = useState(() => state === "error" && !hasArrivedForRunError);
    const isRejected = state === "rejected";
    const isFailed = state === "error" && !hasStaleRunError;
    const isComposing = state === "idle" || state === "error" || isRejected;

    // The run's own country, not the current pick: after a reload or a run adopted from another
    // tab, the two can differ.
    const handOff = useCallback(
        (created: CreatedIndustry) =>
            openCreatedIndustry(created, runCountryId ?? countryId).then((opened) => {
                if (opened) {
                    onIndustryCreated(created, opened.country);
                }

                return opened !== null;
            }),
        [countryId, onIndustryCreated, runCountryId],
    );

    useEffect(() => {
        if (state !== "done") {
            handedOffRef.current = false;
            return;
        }

        if (!industry || handedOffRef.current) {
            return;
        }

        handedOffRef.current = true;
        handOff(industry)
            .then((opened) => {
                if (!opened) {
                    reportHandoffFailure();
                }
            })
            .catch((error) => {
                Sentry.error("CustomIndustryAgent failed to open the created industry", error);

                if (handoffAttempt < MAX_HANDOFF_RETRIES) {
                    handedOffRef.current = false;
                    setHandoffAttempt((attempt) => attempt + 1);
                    return;
                }

                reportHandoffFailure();
            });
    }, [handOff, handoffAttempt, state, industry, reportHandoffFailure]);

    useEffect(() => {
        if (hasStaleRunError) {
            dismissError();
            setHasStaleRunError(false);
        }
    }, [dismissError, hasStaleRunError]);

    // Unlike a rejection, an error is not brought back: leaving the builder lets it go.
    useEffect(() => dismissError, [dismissError]);

    // Once per rejected or failed run, so a country the user picks afterwards is not overwritten.
    useEffect(() => {
        if (!isRejected && !isFailed) {
            hasRestoredCountryRef.current = false;
            return;
        }

        if (hasRestoredCountryRef.current) {
            return;
        }

        hasRestoredCountryRef.current = true;
        const countries: ICountryObject[] = countryUtils.getCountries();
        const runCountry = countries.find((country) => country.id === runCountryId);
        if (runCountry?.permitted && runCountry.id !== countryId) {
            onRestoreCountry(runCountry);
        }
    }, [countryId, isFailed, isRejected, onRestoreCountry, runCountryId]);

    const handleGenerate = useCallback(
        (value: string) => {
            if (isRunBlocking) {
                return;
            }

            if (isRejected || isFailed) {
                dismiss();
            }

            void requestBudget().then(({ maxSegments: budget }) => {
                allTrackers.dispatchTrackEvent("Button", "click", "marketBuilder/v2/generateWithAi", {
                    country: selectedCountry.text,
                    description: value,
                    maxSegments: budget,
                });
                void generate(countryId, value, budget);
            });
        },
        [countryId, selectedCountry.text, dismiss, generate, isFailed, isRejected, isRunBlocking, requestBudget],
    );

    // A failed run that still names its industry created it and only failed to open it, so
    // the way on is opening it, not building it again.
    const handleOpenIndustry = useCallback(() => {
        if (!industry) {
            return;
        }

        handOff(industry).catch((error) => {
            Sentry.error("CustomIndustryAgent failed to open the created industry", error);
        });
    }, [handOff, industry]);

    const getComposeMessage = (): string | null => {
        if (isRejected) {
            return getRejectionComposeMessage(errorCode);
        }

        if (!isFailed) {
            return null;
        }

        return industry
            ? t("market.builder.v2.compose.error.handoffFailed", "Your industry is ready, but opening it failed.")
            : t(
                  "market.builder.v2.compose.error.failed",
                  "Something went wrong while building the industry. Please try again.",
              );
    };

    return isComposing ? (
        <ComposeStep
            onGenerate={handleGenerate}
            onManualFallback={onFallThrough}
            selectedCountry={selectedCountry}
            onSelectedCountryChange={onSelectedCountryChange}
            hasSegmentBudget={isSegmentBudgetLoading || maxSegments !== 0}
            hasSegmentsModule={hasSegmentsModule}
            initialDescription={isRejected || isFailed ? runDescription : ""}
            rejectionMessage={getComposeMessage()}
            bannerAction={
                isFailed && industry
                    ? {
                          label: t("market.builder.agentBanner.error.openIndustry", "Open industry"),
                          onClick: handleOpenIndustry,
                      }
                    : null
            }
        />
    ) : (
        <GeneratingStep
            selectedCountry={selectedCountry}
            onCancel={cancel}
            onClose={onClose}
        />
    );
};
